import React, { useState, useCallback, useMemo } from 'react';
import { Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import AuthWrapper from '../authWrapper';
import LinkComponent from '../../linkComponent';
import CustomButton from '../../button';
import FormInput from '../../inputWrapper/formInput';

import {
  useTemporaryPasswordResetRequestMutation,
  useTemporaryPasswordResetMutation,
  usePasswordResetRequestMutation
} from '../../../redux/slices/apiSlice';

import {
  requiredField,
  passwordComplexity,
  validatePasswordMatch,
  validateEmailInput
} from '../../../helpers/validationHelper';
import { setIsInitialFlow, setRegisteredEmail, setStep } from '../../../redux/slices/modalSlice';
import { useAuth } from '../../../routes/authContext';

import { INPUT_TYPES } from '../../../constants/componentTypes';

import { BRImages } from '../../../assets/imgs';
import MainLoader from '../../loader/mainLoader';

const ForgotPasswordComponent = ({ onBack, isFromRegister, isFromLogin }) => {
  const { openNotification } = useAuth();
  const [sendTemporaryPasswordResetLink, { isLoading: isTemporaryPasswordResetLinkLoading }] =
    useTemporaryPasswordResetMutation();
  const [sendPasswordResetLink, { isLoading: isPasswordResetLinkLoading }] =
    usePasswordResetRequestMutation();
  const [temporaryPasswordResetRequest, { isLoading: isTemporaryPasswordResetRequestLoading }] =
    useTemporaryPasswordResetRequestMutation();
  const dispatch = useDispatch();
  const { step, registeredEmail, temporaryPassword, isInitialFlow } = useSelector(
    (state) => state.modal
  );

  const [form] = Form.useForm();
  const [countdown, setCountdown] = useState(59);
  let isLoadingState =
    isTemporaryPasswordResetRequestLoading ||
    isTemporaryPasswordResetLinkLoading ||
    isPasswordResetLinkLoading;

  const startCountdown = useCallback(() => {
    setCountdown(59);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) clearInterval(interval);
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSendLink = async () => {
    try {
      await form.validateFields();
      const params = { email: form.getFieldValue('email') };

      const response = await temporaryPasswordResetRequest(params);

      if (response?.error) {
        openNotification({
          message: response?.error?.data?.message,
          description: response?.error?.data?.message,
          type: 'error',
          title: 'Error'
        });
        return;
      } else {
        openNotification({
          message: response?.data?.message,
          description: response?.data?.message,
          type: 'success',
          title: 'Success'
        });
      }

      dispatch(setRegisteredEmail(form.getFieldValue('email')));
      dispatch(setStep('checkEmail'));
      startCountdown();
    } catch (error) {
      if (!error.errorFields) {
        console.error('Failed to send link:', error);
        openNotification({
          message: error.response?.data?.message || 'An error occurred while sending the link.',
          type: 'error',
          title: 'Error'
        });
      } else {
        console.error('Validation failed:', error);
      }
    }
  };

  const handleResetPassword = async () => {
    try {
      await form.validateFields();
      const params = {
        temporaryPassword: form.getFieldValue('temporaryPassword'),
        emailOrUsername: registeredEmail,
        newPassword: form.getFieldValue('newPassword')
      };

      let response = {};

      if (isInitialFlow === 'true') {
        response = await sendTemporaryPasswordResetLink(params);
      } else {
        response = await sendPasswordResetLink(params);
      }

      if (response?.error) {
        openNotification({
          message: response?.error?.data?.message,
          type: 'error',
          title: 'Error'
        });
        return;
      } else {
        openNotification({
          message: response?.data?.message,
          type: 'success',
          title: 'Success'
        });
        dispatch(setStep('resetSuccessful'));
        dispatch(setIsInitialFlow(false));
      }
    } catch (error) {
      if (!error.errorFields) {
        console.error('Password reset failed:', error);
        openNotification({
          message: error.response?.data?.message || 'Reset Password Failed',
          type: 'error',
          title: 'Error'
        });
      } else {
        console.error('Validation failed:', error);
      }
    }
  };
  const handleNotRegistered = useCallback(() => {
    dispatch(setStep('notRegistered'));
  }, [dispatch]);

  const handleClearData = useCallback(() => {
    form.resetFields(['newPassword', 'confirmPassword', 'email']);
  }, [form]);

  const renderFormContent = useMemo(() => {
    if (isFromRegister && step === 'checkEmail') {
      return (
        <>
          <div className='big-tick-icon' style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img src={BRImages.CommonImages.EmailBlueLgIcon} alt='tick-icon' />
          </div>
          <p className='text-center'>We have sent a temporary password to </p>
          <p className='user-email text-center m-0 manrope-14-semibold text-opacity-b5'>
            {registeredEmail}
          </p>
        </>
      );
    }
    switch (step) {
      case 'initial':
        return (
          <>
            <FormInput
              label='Email Address'
              name='email'
              rules={[requiredField(''), validateEmailInput]}
              placeholder='Please enter your email'
              onChange={(e) => form.setFieldsValue({ email: e.target.value.trimStart() })}
            />
            <CustomButton
              title='Submit'
              className='mt-16 btn-primary-blue text-white radius-5'
              onClick={handleSendLink}
              loading={isTemporaryPasswordResetRequestLoading}
              disabled={isTemporaryPasswordResetRequestLoading}
            />
            {/* <CustomButton
              title='Clear Data'
              variant='outline'
              className='mt-16 radius-5'
              onClick={handleClearData}
            /> */}
          </>
        );
      case 'checkEmail':
        return (
          <>
            <div className='big-tick-icon' style={{ textAlign: 'center', marginBottom: '20px' }}>
              <img src={BRImages.CommonImages.EmailBlueLgIcon} alt='tick-icon' />
            </div>
            <p className='text-center'>We have sent a temporary password to </p>
            <p className='user-email text-center m-0 manrope-14-semibold text-opacity-b5'>
              {registeredEmail || 'sample@email.com'}
            </p>
            <p className='text-center mt-8 manrope-14-regular text-opacity-b5 mb-0'>
              Didn’t receive an email?{' '}
              <LinkComponent
                title='Resend'
                onClick={handleSendLink}
                className='bookroad-email-resend manrope-14-medium cursor-pointer text-opacity-b5 p-0'
                disabled={countdown > 0}
              />{' '}
              {countdown > 0 ? ` in ${countdown}s` : ''}
            </p>
          </>
        );
      case 'setNewPassword':
        return (
          <>
            <FormInput
              label='Temporary Password'
              name='temporaryPassword'
              placeholder='Enter your temporary password'
              type={INPUT_TYPES.PASSWORD}
              onChange={(e) => form.setFieldsValue({ temporaryPassword: e.target.value.trim() })}
              tooltipText='Temporary password is a one-time password sent to your email.'
            />
            <FormInput
              label='New Password'
              name='newPassword'
              rules={[requiredField(''), passwordComplexity]}
              placeholder='Enter your new password'
              type={INPUT_TYPES.PASSWORD}
              onChange={(e) => form.setFieldsValue({ newPassword: e.target.value.trim() })}
              tooltipText={
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Must be 6–15 characters long.</li>
                  <li>Include at least one uppercase letter (A–Z).</li>
                  <li>Include at least one lowercase letter (a–z).</li>
                  <li>Include at least one number (0–9).</li>
                  <li>Include at least one special character (e.g. @$!%*?&).</li>
                  <li>Only letters, digits, and the specified special characters are allowed.</li>
                  <li>Avoid common words or easily guessed phrases.</li>
                </ul>
              }
            />
            <FormInput
              label='Confirm Password'
              name='confirmPassword'
              dependencies={['newPassword']}
              type={INPUT_TYPES.PASSWORD}
              rules={[
                requiredField(''),
                ({ getFieldValue }) => validatePasswordMatch('newPassword', getFieldValue)
              ]}
              placeholder='Confirm your new password'
              onChange={(e) => form.setFieldsValue({ confirmPassword: e.target.value.trim() })}
              tooltipText='Re-enter your password to confirm.'
            />
            <CustomButton
              title={isInitialFlow === 'true' ? 'Set New Password' : 'Reset Password'}
              className='btn-primary-blue text-white radius-5'
              onClick={handleResetPassword}
              loading={
                isInitialFlow === 'true'
                  ? isTemporaryPasswordResetLinkLoading
                  : isPasswordResetLinkLoading
              }
              disabled={
                isInitialFlow === 'true'
                  ? isTemporaryPasswordResetLinkLoading
                  : isPasswordResetLinkLoading
              }
            />
            {/* <CustomButton
              title='Clear Data'
              variant='outline'
              className='mt-16 radius-5'
              onClick={handleClearData}
            /> */}
          </>
        );
      case 'resetSuccessful':
        return (
          <>
            <p className='text-center'>
              {isFromLogin
                ? 'Your password has been successfully created.'
                : 'Your password has been successfully reset.'}
            </p>
            <CustomButton
              title='Sign In'
              className='btn-primary-blue text-white radius-5'
              onClick={onBack}
            />
          </>
        );
      case 'notRegistered':
        return (
          <CustomButton
            title='Sign UP'
            className='btn-primary-blue text-white radius-5'
            onClick={handleNotRegistered}
          />
        );
      default:
        return null;
    }
  }, [
    step,
    handleSendLink,
    handleResetPassword,
    handleClearData,
    onBack,
    isFromRegister,
    isFromLogin,
    registeredEmail,
    handleNotRegistered,
    countdown
  ]);

  return (
    <AuthWrapper
      key={step}
      title={
        isFromLogin && step === 'resetSuccessful'
          ? 'Password Created Successfully'
          : step === 'initial'
            ? 'Forgot Password?'
            : step === 'checkEmail'
              ? 'Check Your Email'
              : step === 'setNewPassword'
                ? 'Set New Password'
                : step === 'notRegistered'
                  ? 'Please Register'
                  : 'Password Reset Successful'
      }
      note={
        isFromRegister && step === 'checkEmail' ? (
          ''
        ) : step === 'initial' ? (
          'Enter your email that you used to register your account, so we can send you a link to reset your password.'
        ) : step === 'checkEmail' ? (
          ''
        ) : step === 'setNewPassword' ? (
          <p className='text-center'>
            Your new password must be different from previously used passwords.
          </p>
        ) : step === 'notRegistered' ? (
          <p className='text-center'>
            'Entered email is not registered.Please sign up for the application.
          </p>
        ) : (
          ''
        )
      }
    >
      {isLoadingState && (
        <div className='loading-overlay'>
          <MainLoader style={{ height: 150, width: 150 }} />
        </div>
      )}
      <Form
        form={form}
        layout='vertical'
        className='signin-form mt-24'
        requiredMark={false}
        initialValues={{
          temporaryPassword: temporaryPassword
        }}
      >
        {renderFormContent}
      </Form>
    </AuthWrapper>
  );
};

export default ForgotPasswordComponent;
